import React, {
  useMemo,
  useRef,
  useState,
} from 'react';
import ButtonGroup from '@mui/material/ButtonGroup';
import {
  useGetPastVisitNotesForPatient,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import ToolTip from 'core/components/Tooltip';
import {
  useMixpanelTrack,
} from 'core/hooks';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Button from 'pages/Dashboard/components/Button';
import SaltIcon from 'core/icons/salt';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import Spinner from 'pages/Dashboard/components/Spinner';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import SaltMenu from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/SaltMenu';
import head from 'lodash/head';
import {
  getsSaltButtonDisabledTooltip,
  isSaltContentAvailable,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/helper';
import {
  isEmptyString,
} from '@populateui/utils';

type Props = {
  patientId: number;
  visitNoteId: number;
  disabled: boolean;
};

export default function ExtendedVisitNoteSalt({
  patientId,
  visitNoteId,
  disabled: _disabled,
}: Props) {
  const mixpanelTrack = useMixpanelTrack();

  const {
    data: pastVisitNotes = [],
    isLoading: loadingPastVisitNote,
    isFetched: fetchedPastVisitNote,
  } = useGetPastVisitNotesForPatient(patientId, visitNoteId);

  const hasPastVisitNotes = useMemo(() => pastVisitNotes?.length > 0, [pastVisitNotes]);

  const mostRecentValidVisitNoteId = useMemo(
    () => head(
      pastVisitNotes.filter(
        (note) => isSaltContentAvailable(note, 'master'),
      ),
    )?.visitNoteId,
    [pastVisitNotes],
  );

  const hasValidPastVisitNote = !isNil(mostRecentValidVisitNoteId);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const handleOpen = () => {
    setAnchorElement(anchorRef.current);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleApply = (id: number | null, source: string) => {
    if (!isNil(id)) {
      visitNoteEventChannel.emit('onApplyPastNote', id);
      mixpanelTrack('clickedMasterSaltButton', { type: source });
    }
  };

  const disabled = useMemo(
    () => !hasValidPastVisitNote || _disabled || loadingPastVisitNote || !hasPastVisitNotes,
    [hasValidPastVisitNote, _disabled, loadingPastVisitNote, hasPastVisitNotes],
  );

  const tooltipMessage = useMemo(() => {
    if (!fetchedPastVisitNote) return null;
    if (!hasPastVisitNotes) return 'There is no prior visit note';
    if (!hasValidPastVisitNote) return getsSaltButtonDisabledTooltip('master');
    return null;
  }, [fetchedPastVisitNote, hasPastVisitNotes, hasValidPastVisitNote]);

  return (
    <ToolTip
      hide={isEmptyString(tooltipMessage)}
      title={tooltipMessage}
    >
      <ButtonGroup
        variant="outlined"
        className={clsx({ 'cursor-not-allowed': !hasPastVisitNotes })}
      >
        <Button
          ref={anchorRef}
          className="nw-master-same-as-last-time !px-2.5 !py-0.5 h-6"
          variant="outlined"
          disabled={disabled}
          onClick={() => {
            handleApply(mostRecentValidVisitNoteId ?? null, 'Same as Last Time');
          }}
        >
          <SaltIcon
            className="!w-4 !h-4 !mr-1.5 !text-inherit"
          />
          Same as Last Time
        </Button>
        <Button
          aria-haspopup="menu"
          variant="outlined"
          disabled={disabled}
          onClick={handleOpen}
          className="!p-0.5"
        >
          {loadingPastVisitNote
            ? <Spinner />
            : <KeyboardArrowDownRoundedIcon className="!w-4 !h-4" />}
        </Button>
        <SaltMenu
          pastVisitNotes={pastVisitNotes}
          saltType="master"
          anchorElement={anchorElement}
          onClose={handleClose}
          onClickMenuItem={(id) => {
            handleClose();
            handleApply(id, 'Same as Last Time from salt menu');
          }}
        />
      </ButtonGroup>
    </ToolTip>
  );
}
